import React, { useEffect, useState } from "react";
import ExportButton from "./ExportButton";
import { useListParams } from "hooks/useListParams";
import Pagination from "./common/Pagination";
import Drawer, { FilterDrawer } from "./common/Drawer";
import SendSmsSidebar from "./SendSmsSidebar";
import ApiKeySelect from "./common/ApiKeySelect";
import { apiKeyLabel, useApiKeyNames } from "hooks/useApiKeyNames";
import { useParams } from "react-router-dom";
import { CustomerService } from "services/CustomerService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetSmsMessageDto } from "services/Dtos/GetSmsMessagesDto";
import SmsHistorySidebar from "./SmsHistorySidebar";

type SortField =
  | "recipientNumber"
  | "senderNumber"
  | "body"
  | "timestamp"
  | "lastStatus";

interface Props {
  customerId?: string;
}

const PAGE_SIZE = 10;

const emptyFilters = { apiKeyId: '', recipientNumbers: '', senderNumbers: '', status: '', messageBody: '', dateFrom: '', dateTo: '' };

const CustomerSmsMessages: React.FC<Props> = ({ customerId: propCustomerId }) => {
  const { customerId: paramCustomerId } = useParams<{ customerId: string }>();
  const customerId = propCustomerId ?? paramCustomerId;
  const [smsMessages, setSmsMessages] = useState<GetSmsMessageDto[]>([]);
  const apiKeyNames = useApiKeyNames(smsMessages.map((message) => message.apiKeyId));
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { pageNumber, setPageNumber, sortField, sortDirection, setSort, appliedFilters, setAppliedFilters } =
    useListParams<typeof emptyFilters, SortField>(emptyFilters, { field: "timestamp", direction: "desc" });
  const [selectedMessage, setSelectedMessage] = useState<GetSmsMessageDto | null>(null);
  const [previewMessage, setPreviewMessage] = useState<GetSmsMessageDto | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [selectedPhoneForBlock, setSelectedPhoneForBlock] = useState<{
    number: string;
    isBlocked: boolean;
    apiKeyId: number;
  } | null>(null);
  const [blockReason, setBlockReason] = useState("");

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // The API key is picked from its own dropdown on the page, not the filter panel.
  const activeFilterCount = Object.entries(appliedFilters).filter(([key, v]) => key !== 'apiKeyId' && v !== '').length;

  const [historyApiKey, setHistoryApiKey] = useState<string>("");

  // The history endpoint needs the X-API-Key of the key the SMS was sent with
  useEffect(() => {
    setHistoryApiKey("");
    const apiKeyId = selectedMessage?.apiKeyId;
    if (!apiKeyId) return;
    CustomerService.apiKey
      .getApiKeyValue(apiKeyId)
      .then(setHistoryApiKey)
      .catch((err) => {
        console.error("Failed to fetch API key:", err);
        toast.error("Failed to load SMS history");
        setSelectedMessage(null);
      });
  }, [selectedMessage]);

  const filterArgs = [
    appliedFilters.recipientNumbers || undefined,
    appliedFilters.senderNumbers || undefined,
    appliedFilters.status || undefined,
    appliedFilters.messageBody || undefined,
    appliedFilters.dateFrom || undefined,
    appliedFilters.dateTo || undefined,
  ] as const;

  const fetchSmsMessages = async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      // With an API key selected, list only that key's messages; otherwise the whole company's.
      const response = appliedFilters.apiKeyId
        ? await CustomerService.sms.getCustomerSmsMessagesByApiKeyId(
            parseInt(appliedFilters.apiKeyId), sortField, sortDirection === "desc", pageNumber, PAGE_SIZE, ...filterArgs,
          )
        : await CustomerService.sms.getCustomerSmsMessagesByCustomerId(
            parseInt(customerId), sortField, sortDirection === "desc", pageNumber, PAGE_SIZE, ...filterArgs,
          );
      setSmsMessages(response.data.items || []);
      setTotalCount(response.data.totalCount || 0);
    } catch {
      setError("Failed to load SMS history");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId) return;
    const timer = setTimeout(fetchSmsMessages, 0);
    return () => clearTimeout(timer);
  }, [customerId, appliedFilters, sortField, sortDirection, pageNumber]);

  const handleTempFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    setTempFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
    setAppliedFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
  };

  const handleSort = (field: SortField) => {
    setSort(field, field === sortField && sortDirection === "asc" ? "desc" : "asc");
  };

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <i className="bi bi-caret-up-fill ms-1"></i>
    ) : (
      <i className="bi bi-caret-down-fill ms-1"></i>
    );
  };

  const getStatusBadgeClass = (status: string): string => {
    const s = status.toLowerCase();
    if (s === "processed" || s === "queued" || s === "processing") return "warning";
    if (s === "sent" || s === "delivered") return "success";
    if (s === "failed" || s === "bounce") return "danger";
    return "primary";
  };

  const handleBlockPhoneNumber = async (phoneNumber: string, shouldBlock: boolean, apiKeyId: number) => {
    try {
      const apiKey = await CustomerService.apiKey.getApiKeyValue(apiKeyId);
      await CustomerService.sms.updateBlockStatus(
        { phoneNumber, shouldBlock, reason: blockReason },
        apiKey,
      );
      toast.success(`Phone number ${shouldBlock ? "blocked" : "unblocked"} successfully`);
      setBlockModalOpen(false);
      setBlockReason("");
      setSelectedPhoneForBlock(null);
      fetchSmsMessages();
    } catch {
      toast.error(`Failed to ${shouldBlock ? "block" : "unblock"} phone number`);
    }
  };

  const openBlockModal = (number: string, isBlocked: boolean, apiKeyId: number) => {
    setSelectedPhoneForBlock({ number, isBlocked, apiKeyId });
    setBlockModalOpen(true);
  };

  const handleExport = async (format: string) => {
    if (!customerId) return;
    setIsExporting(true);
    try {
      const response = appliedFilters.apiKeyId
        ? await CustomerService.sms.exportSmsMessagesByApiKeyId(
            parseInt(appliedFilters.apiKeyId), sortField, sortDirection === "desc", ...filterArgs, format,
          )
        : await CustomerService.sms.exportSmsMessagesByCustomerId(
            parseInt(customerId), sortField, sortDirection === "desc", ...filterArgs, format,
          );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `sms-messages.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export SMS messages");
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return <div className="alert alert-danger m-4" role="alert">{error}</div>;
  }

  return (
    <div className="container-fluid p-4">

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">SMS Messages</h3>
          <p className="text-muted mb-0">View and manage SMS message history</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ApiKeySelect
            id="sms-messages-api-key"
            customerId={customerId}
            value={appliedFilters.apiKeyId}
            onChange={(apiKeyId) => setAppliedFilters({ ...appliedFilters, apiKeyId })}
          />
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={isLoading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button className="btn btn-outline-primary btn-sm" onClick={fetchSmsMessages} disabled={isLoading}>
            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
            Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setIsSendOpen(true)}>
            <i className="bi bi-send-plus me-1"></i>Send SMS
          </button>
        </div>
      </div>

      <Drawer isOpen={isSendOpen} onClose={() => setIsSendOpen(false)} title="Send SMS" icon="send-plus" width={480}>
        <SendSmsSidebar
          customerId={customerId ?? ""}
          defaultApiKeyId={appliedFilters.apiKeyId}
          onClose={() => setIsSendOpen(false)}
          onSend={fetchSmsMessages}
        />
      </Drawer>

      <FilterDrawer
        title="Filter SMS Messages"
        width={500}
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={handleClearAllFilters}
        onApply={handleApplyFilters}
      >
        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Recipient Number</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by recipient..."
              name="recipientNumbers"
              value={tempFilters.recipientNumbers}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Sender Number</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by sender..."
              name="senderNumbers"
              value={tempFilters.senderNumbers}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Message Body</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by message content..."
              name="messageBody"
              value={tempFilters.messageBody}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Status</label>
          <select
            className="form-select"
            name="status"
            value={tempFilters.status}
            onChange={handleTempFilterChange}
          >
            <option value="">All</option>
            <option value="queued">Queued</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="bounce">Bounce</option>
          </select>
        </div>

        <hr className="my-3" />

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Date From</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="dateFrom"
              value={tempFilters.dateFrom}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Date To</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="dateTo"
              value={tempFilters.dateTo}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>
      </FilterDrawer>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : smsMessages.length === 0 ? (
        <div className="alert alert-info">
          {activeFilterCount > 0 ? "No SMS messages match your search criteria." : "No SMS history found."}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th onClick={() => handleSort("recipientNumber")} style={{ cursor: "pointer" }}>
                    Recipient {renderSortIndicator("recipientNumber")}
                  </th>
                  <th onClick={() => handleSort("senderNumber")} style={{ cursor: "pointer" }}>
                    Sender {renderSortIndicator("senderNumber")}
                  </th>
                  <th onClick={() => handleSort("body")} style={{ cursor: "pointer" }}>
                    Message {renderSortIndicator("body")}
                  </th>
                  <th onClick={() => handleSort("timestamp")} style={{ cursor: "pointer" }}>
                    Time {renderSortIndicator("timestamp")}
                  </th>
                  <th onClick={() => handleSort("lastStatus")} style={{ cursor: "pointer" }}>
                    Status {renderSortIndicator("lastStatus")}
                  </th>
                  <th>API Key</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {smsMessages.map((message) => (
                  <tr key={message.id}>
                    <td>{message.recipientNumber}</td>
                    <td>{message.senderNumber}</td>
                    <td
                      style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}
                      title={message.body}
                    >
                      {message.body}
                    </td>
                    <td>{new Date(message.createdAt).toLocaleString()}</td>
                    <td>
                      <span className={`badge bg-${getStatusBadgeClass(message.lastStatus)}`}>
                        {message.lastStatus}
                      </span>
                    </td>
                    <td className="text-nowrap">{apiKeyLabel(apiKeyNames, message.apiKeyId)}</td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => setSelectedMessage(message)}
                          title="View History"
                        >
                          <i className="bi bi-clock-history"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => setPreviewMessage(message)}
                          title="Preview Message"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openBlockModal(message.recipientNumber, message.phoneNumber.isBlocked, message.apiKeyId)}
                          title={message.phoneNumber.isBlocked ? `Blocked: ${message.phoneNumber.blockReason || "No reason provided"}` : "Block Phone Number"}
                        >
                          <i className={`bi bi-${message.phoneNumber.isBlocked ? "unlock" : "lock"}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination pageNumber={pageNumber} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPageNumber} />
        </>
      )}

      {/* SMS History Sidebar */}
      {selectedMessage && historyApiKey && (
        <SmsHistorySidebar
          isOpen={true}
          onClose={() => setSelectedMessage(null)}
          smsId={selectedMessage.id}
          message={selectedMessage.body}
          apiKey={historyApiKey}
        />
      )}

      {/* SMS Preview Modal */}
      {previewMessage && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">SMS Preview</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewMessage(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3"><strong>From:</strong> {previewMessage.senderNumber}</div>
                <div className="mb-3"><strong>To:</strong> {previewMessage.recipientNumber}</div>
                <div className="mb-3">
                  <strong>Status:</strong>{" "}
                  <span className={`badge bg-${getStatusBadgeClass(previewMessage.lastStatus)}`}>
                    {previewMessage.lastStatus}
                  </span>
                </div>
                <div className="mb-3"><strong>Sent:</strong> {new Date(previewMessage.createdAt).toLocaleString()}</div>
                <div className="border rounded p-3 bg-light">
                  <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{previewMessage.body}</pre>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewMessage(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Modal */}
      {blockModalOpen && selectedPhoneForBlock && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedPhoneForBlock.isBlocked ? "Unblock Phone Number" : "Block Phone Number"}</h5>
                <button type="button" className="btn-close" onClick={() => { setBlockModalOpen(false); setSelectedPhoneForBlock(null); setBlockReason(""); }}></button>
              </div>
              <div className="modal-body">
                {!selectedPhoneForBlock.isBlocked && (
                  <div className="mb-3">
                    <label htmlFor="blockReason" className="form-label">Reason for blocking</label>
                    <textarea
                      className="form-control"
                      id="blockReason"
                      rows={3}
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Enter reason for blocking this phone number"
                      required
                    />
                  </div>
                )}
                <p>
                  Are you sure you want to {selectedPhoneForBlock.isBlocked ? "unblock" : "block"} the phone number{" "}
                  <strong>{selectedPhoneForBlock.number}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setBlockModalOpen(false); setSelectedPhoneForBlock(null); setBlockReason(""); }}>Cancel</button>
                <button
                  type="button"
                  className={`btn btn-${selectedPhoneForBlock.isBlocked ? "success" : "danger"}`}
                  onClick={() => handleBlockPhoneNumber(selectedPhoneForBlock.number, !selectedPhoneForBlock.isBlocked, selectedPhoneForBlock.apiKeyId)}
                  disabled={!selectedPhoneForBlock.isBlocked && !blockReason.trim()}
                >
                  {selectedPhoneForBlock.isBlocked ? "Unblock" : "Block"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSmsMessages;
